import { useState } from 'react';
import { Save, Check, AlertCircle, LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import { reportsService, SaveReportData } from '../lib/reportsService';

interface SaveReportButtonProps {
  reportData: SaveReportData;
  onSaveSuccess?: () => void;
  className?: string;
}

export default function SaveReportButton({ reportData, onSaveSuccess, className = '' }: SaveReportButtonProps) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNeedsAuth(false);

    const isAuth = await reportsService.isAuthenticated();
    if (!isAuth) {
      setSaving(false);
      setNeedsAuth(true);
      return;
    }

    const { data, error: saveError } = await reportsService.saveReport(reportData);
    setSaving(false);

    if (saveError) {
      if (saveError.message.includes('log in')) {
        setNeedsAuth(true);
      } else {
        setError(saveError.message);
      }
      return;
    }

    if (data) {
      setSaved(true);
      onSaveSuccess?.();
      setTimeout(() => setSaved(false), 5000);
    }
  };

  if (needsAuth) {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        <div className="flex items-center gap-2 text-amber-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>Please log in to save reports</span>
        </div>
        <Link to="/auth/login" className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
          <LogIn className="w-4 h-4" />
          Log In
        </Link>
      </div>
    );
  }

  if (saved) {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        <button disabled className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium">
          <Check className="w-4 h-4" />
          Report Saved!
        </button>
        <Link to="/reports" className="text-sm text-green-600 hover:text-green-700 text-center">
          View Saved Reports →
        </Link>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <button onClick={handleSave} disabled={saving} className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white rounded-lg transition-colors font-medium text-sm">
        <Save className="w-4 h-4" />
        {saving ? 'Saving...' : 'Save Report'}
      </button>
      {error && (
        <span className="flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="w-3 h-3" />
          {error}
        </span>
      )}
    </div>
  );
}
